import { isEmptyContent } from './contentUtils'
import {
  PlaceholderStrategy,
  HeadshotStrategy,
  ComplexPlaceholderStrategy,
  SimpleTextStrategy,
} from './testimonialPlaceholders'

interface RenderableTestimonial {
  testimonial_text: string
  question_text: string | null
  attribution_line_1: string | null
  attribution_line_2: string | null
  attribution_line_3: string | null
  nps_score: number | null
  avatar_url?: string | null
}

/**
 * SvgTestimonial - class for rendering testimonial content into SVG templates
 *
 * This class takes an SVG template with placeholders and replaces them with
 * actual testimonial content. It supports conditional elements, text replacement,
 * and complex structures requiring foreign objects.
 */
export default class SvgTestimonial {
  svgTemplate: string
  renderableTestimonial: RenderableTestimonial
  svgDoc: Document
  conditionalPrefix = 'IF_'
  placeholders = {
    QUOTE_TEXT: 'testimonial_text',
    QUESTION_TEXT: 'question_text',
    ATTRIBUTION_1: 'attribution_line_1',
    ATTRIBUTION_2: 'attribution_line_2',
    ATTRIBUTION_3: 'attribution_line_3',
    NPS_SCORE: 'nps_score',
    HEADSHOT: 'avatar_url',
  }
  strategies: PlaceholderStrategy[]

  constructor(svgTemplate: string, renderableTestimonial: RenderableTestimonial) {
    this.svgTemplate = svgTemplate
    this.renderableTestimonial = renderableTestimonial
    this.svgDoc = new DOMParser().parseFromString(this.svgTemplate, 'image/svg+xml')

    // Initialize strategies for different placeholder types
    this.strategies = [
      new HeadshotStrategy(),
      new ComplexPlaceholderStrategy(),
      new SimpleTextStrategy(),
    ]
  }

  /**
   * Generate the final SVG markup with all placeholders replaced
   */
  markup(): string {
    this.processConditionalElements()
    this.replaceAllPlaceholders()
    return this.svgDoc.documentElement.outerHTML
  }

  /**
   * Process conditional elements with the IF_ prefix
   * Only keeps elements if their corresponding data is not empty
   */
  processConditionalElements(): void {
    // Get all elements with IDs that start with the conditional prefix
    const elements = Array.from(this.svgDoc.querySelectorAll(`[id^="${this.conditionalPrefix}"]`))

    elements.forEach((element) => {
      const id = element.id
      // Extract the field name from the ID by removing the prefix
      const field = id.substring(this.conditionalPrefix.length)

      // Map to the actual data field if it's in our placeholders mapping
      const dataField = this.getDataFieldForPlaceholder(field)

      // Only process if we found a matching placeholder field
      if (dataField) {
        // Get the value from the testimonial data
        const value = this.renderableTestimonial[dataField]

        // If the content is empty, remove the element
        if (isEmptyContent(value)) {
          this.removeElement(element)
        }
      } else {
        // If we can't find a matching field, remove the element since we can't evaluate it
        this.removeElement(element)
      }
    })
  }

  /**
   * Replace all placeholders with their actual content
   */
  replaceAllPlaceholders(): void {
    Object.entries(this.placeholders).forEach(([placeholderId, contentKey]) => {
      const value = this.renderableTestimonial[contentKey]
      // Convert number values to string before passing to replacePlaceholder
      const content = typeof value === 'number' ? String(value) : value
      this.replacePlaceholder(placeholderId, content)
    })
  }

  /**
   * Replace a single placeholder with its content
   */
  replacePlaceholder(placeholderId: string, content: string | null): void {
    const element = this.svgDoc.getElementById(placeholderId)
    if (!element) {
      return
    }

    // If content is empty, remove the element entirely
    if (isEmptyContent(content)) {
      this.removeElement(element)
      return
    }

    // Find the first strategy that can handle this placeholder
    const strategy = this.findStrategyForPlaceholder(element, placeholderId)

    // Apply the strategy to process the placeholder
    strategy.process(element, content, this.svgDoc)
  }

  /**
   * Find the appropriate strategy for handling a placeholder
   */
  private findStrategyForPlaceholder(element: Element, placeholderId: string): PlaceholderStrategy {
    for (const strategy of this.strategies) {
      if (strategy.canHandle(element, placeholderId)) {
        return strategy
      }
    }

    // Default to simple text strategy if no other strategy matches
    return this.strategies[this.strategies.length - 1]
  }

  /**
   * Get the data field name corresponding to a placeholder ID
   */
  private getDataFieldForPlaceholder(placeholderId: string): string | undefined {
    return Object.entries(this.placeholders).find(([id]) => id === placeholderId)?.[1]
  }

  /**
   * Remove an element from the DOM
   */
  private removeElement(element: Element): void {
    element.parentNode?.removeChild(element)
  }
}
